import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { AccountCreation } from "@/components/signup/account-creation/account-creation";
import { Input } from "@/components/signup/account-creation/components/input/input.tsx";
import { Social } from "@/components/signup/account-creation/components/social/social.tsx";
import { useDeferSiteNamingContinueUrl } from "@/components/signup/hooks/use-defer-site-naming-continue-url.ts";
import { SocialProvider } from "@/components/signup/utils/get-social-signup-button-urls.tsx";
import { wacToken } from "@/libraries/design/foundations";
import { experiments } from "@/utils/experiment/manifest.ts";
import { useExperiment } from "@/utils/experiment/use-experiment.ts";
import { useLocation } from "@/utils/location-provider";
import { getWacIntegrationUrl } from "@/utils/urls.ts";
import { useWacRefresh } from "@/utils/wac-refresh/wac-refresh-context";

import { InlineInputSubmitSocial } from "./components/inline-account-creation/inline-input-submit-social.tsx";

export type SignUpFormTextColor = "default" | "light";

export type UniformSignUpProps = {
    socialProviders?: SocialProvider[];
    helpText?: string;
    textColor?: SignUpFormTextColor;
    inline?: boolean;
    continueUrl?: string;
};

export const transformProviders = (providerList: string[]) => {
    return providerList.reduce(
        (previous, key) => ({ ...previous, [key.toLowerCase()]: key }),
        {}
    );
};

const SERVICE_COLLECTION_COBRANDING = "wac--service-collection-jsm-cobranding";
const JSM_SERVICE_MANAGEMENT_PATH = "/software/jira/service-management";

const UniformSignUpForm = ({
    socialProviders,
    helpText,
    textColor = "default",
    inline = false,
    continueUrl
}: // eslint-disable-next-line complexity
UniformSignUpProps) => {
    const intl = useIntl();
    const isWacRefresh = useWacRefresh();
    const location = useLocation();

    const isJsmServiceManagementPage = location.pathname.includes(
        JSM_SERVICE_MANAGEMENT_PATH
    );

    const servCoLofiBackgroundExperiment = useExperiment(
        experiments.servCoLofiBackground
    );
    const servCoLofiBackground =
        servCoLofiBackgroundExperiment.type === "enrolled"
            ? servCoLofiBackgroundExperiment.value
            : experiments.servCoLofiBackground.defaultValue;

    // Defer site naming continueUrl override — only fires for Jira variant users
    // (the hook handles product-key gating internally).
    const getContinueUrlFromHook = useDeferSiteNamingContinueUrl();

    // The experiment hook takes precedence. If no experiment URL is provided,
    // fall back to the Uniform-configured continueUrl (if any), otherwise
    // AccountCreation uses its own default.
    const getContinueUrl = continueUrl
        ? () => continueUrl?.startsWith("/") ? getWacIntegrationUrl() + continueUrl : continueUrl
        : getContinueUrlFromHook;

    const defaultHelpText = intl.formatMessage({
        id: "signupForm.accountCreation.helperTextTeam26",
        defaultMessage:
            "Use a work email to find teammates and get access to Rovo AI"
    });
    const configuredHelpText = helpText?.trim();
    const emailHelpText = configuredHelpText || defaultHelpText;

    return (
        <div css={[textColor === "light" && lightTextColorCss]}>
            <AccountCreation
                identityCobranding={
                    servCoLofiBackground && isJsmServiceManagementPage
                        ? SERVICE_COLLECTION_COBRANDING
                        : undefined
                }
                getContinueUrl={getContinueUrl}
                Login={null}
                Separator={inline ? null : undefined}
                Error={inline ? null : undefined}
                Input={
                    inline ? (
                        <InlineInputSubmitSocial
                            helpText={configuredHelpText}
                        />
                    ) : isWacRefresh ? (
                        <Input helpText={emailHelpText} />
                    ) : undefined
                }
                Submit={inline ? null : undefined}
                Social={
                    inline ? null : socialProviders?.length ? (
                        <Social
                            providers={transformProviders(socialProviders)}
                        />
                    ) : undefined
                }
            />
        </div>
    );
};

export default UniformSignUpForm;

const lightTextColorCss = css({
    color: wacToken("color.neutral.light0")
});
